import { faCalendarDays } from '@fortawesome/pro-regular-svg-icons';
import * as React from 'react';
import moment from 'moment';
import classNames from 'classnames';
import Button from '../../atoms/Button';
import DatePicker from '../DatePicker';
import FilterControl from '../../atoms/FilterControl';
import Overlay from '../../atoms/Overlay';
import Surface from '../../atoms/Surface';

import styles from './YearFilter.module.scss';

export interface Props
  extends Omit<React.HTMLAttributes<HTMLElement>, 'onSubmit'> {
  className?: string;
  style?: React.CSSProperties;
  from: string;
  to: string;
  onSubmit: (from: string, to: string) => void;
  minDate?: Date;
  maxDate?: Date;
}

interface YearSelection {
  from: string;
}

/**
 * <YearFilter> component.
 */
export const YearFilter: React.FunctionComponent<Props> = ({
  className,
  style,
  from,
  to,
  onSubmit,
  maxDate,
  minDate,
  ...htmlElementProps
}) => {
  const [show, setShow] = React.useState(false);
  const [target, setTarget] = React.useState(null);
  const [selectedYear, setSelectedYear] = React.useState(
    moment(from).format('YYYY'),
  );

  /**
   * Handles the year selection from picker.
   */
  const handleFilterChange = React.useCallback((vars: YearSelection) => {
    const { from: selectedFrom } = vars;
    setSelectedYear(moment(selectedFrom).format('YYYY'));
  }, []);

  const handleCancel = React.useCallback(() => {
    setSelectedYear(moment(from).format('YYYY'));
    setShow(false);
  }, [from]);

  const handleSubmit = React.useCallback(() => {
    const rangeFrom = moment(`${selectedYear}-01-01`)
      .startOf('year')
      .toISOString();
    const rangeTo = moment(`${selectedYear}-12-31`).endOf('year').toISOString();
    onSubmit(rangeFrom, rangeTo);
    setShow(false);
  }, [onSubmit, selectedYear]);

  React.useEffect(() => {
    setSelectedYear(moment(from).format('YYYY'));
  }, [from]);

  const rangeStart = React.useMemo(
    () => moment(`${selectedYear}-01-01`).startOf('year').toISOString(),
    [selectedYear],
  );

  const rangeEnd = React.useMemo(
    () => moment(`${selectedYear}-12-31`).endOf('year').toISOString(),
    [selectedYear],
  );

  return (
    <div {...htmlElementProps}>
      <FilterControl
        className={classNames(styles.wrapper, className)}
        style={style}
        ref={setTarget as React.Ref<HTMLDivElement>}
        prepend={faCalendarDays}
        placeholder={selectedYear}
        onClick={() => setShow(!show)}
        readOnly
      />

      <Overlay
        target={target}
        placement="bottom-end"
        show={show}
        onHide={() => setShow(false)}
      >
        <Surface
          className="d-flex flex-column"
          backgroundColor="grayLight3"
          padding="sm"
        >
          <DatePicker
            showNavigation={false}
            calendarClassName={styles.calendar}
            from={rangeStart}
            to={rangeEnd}
            onChange={handleFilterChange}
            maxDate={maxDate}
            minDate={minDate}
            view="decade"
            maxDetail="decade"
          />

          <div className="d-flex flex-justify-end gap-2">
            <Button variant="outline" onClick={handleCancel}>
              Cancelar
            </Button>
            <Button onClick={handleSubmit}>Aplicar</Button>
          </div>
        </Surface>
      </Overlay>
    </div>
  );
};

export default YearFilter;
